<template>
    <q-layout view="hHh lpR fFf">
        <!-- TOP PANEL -->
        <nav-bar
            id="menu-button"
            @menuClick="showLeftPanel = !showLeftPanel"
            @navigate="handleRouting"
            class="navbar"
        />

        <!-- LEFT PANEL -->
        <q-drawer
            v-model="showLeftPanel"
            :elevated="true"
            :width="250"
            side="left"
            background="white"
            bordered
            :behavior="$q.screen.lt.lg ? 'mobile' : 'default'"
            class="left-panel"
        >
            <!-- Facility list -->
            <q-list padding>
                <div v-if="facilities && facilities.length > 0">
                    <q-item-label header>{{ $t('Facility') }}</q-item-label>
                    <q-item-section class="q-mx-md">
                        <q-select
                            v-model="dropdownOption"
                            :options="facilityOptions"
                            color="accent"
                            borderless
                            dense
                        >
                            <template v-slot:prepend>
                                <q-icon name="fa fa-industry-alt" />
                            </template>
                        </q-select>
                    </q-item-section>
                </div>
                <div v-else class="q-pa-md">
                    {{ $t('No facilities loaded.') }}
                </div>
            </q-list>

            <!-- Asset list  -->
            <div v-if="dropdownOption.value !== null">
                <q-separator spaced />
                <q-item-label header>{{ $t('Assets') }}</q-item-label>
                <q-list v-for="(asset, index) in assets" :key="index">
                    <q-item
                        :id="'asset-item-' + asset.id"
                        v-ripple
                        :active="
                            selectedAsset
                                ? selectedAsset.id === asset.id
                                : false
                        "
                        active-class="active-asset"
                        clickable
                        @click="
                            handleRouting({
                                label: asset.name,
                                name: 'asset_dashboard',
                                params: {
                                    assetId: asset.id,
                                    facilityId: selectedFacility.id
                                }
                            })
                        "
                    >
                        <q-item-section avatar>
                            <q-avatar
                                size="md"
                                icon="fas fa-conveyor-belt"
                                color="secondary"
                                text-color="white"
                            />
                        </q-item-section>
                        <q-item-section>{{ asset.name }}</q-item-section>
                        <q-item-section side>
                            <q-icon
                                :name="getDrawerItemStyle(asset.id)['icon']"
                                :color="getDrawerItemStyle(asset.id)['color']"
                            />
                        </q-item-section>
                    </q-item>
                </q-list>
                <div v-if="assets.length === 0" class="q-pa-md">
                    {{ $t('No assets loaded.') }}
                </div>
            </div>
        </q-drawer>

        <!-- VIEW CONTAINER -->
        <q-page-container
            :class="
                $q.screen.lt.sm
                    ? 'safe-area-margin--mobile'
                    : 'safe-area-margin'
            "
        >
            <!-- Global banners -->
            <global-banners
                :signal-r-connection-failed-banner="
                    signalRConnectionFailedBanner
                "
                :subscribe-banner="subscribeBanner"
                :reconnecting-banner="reconnectingBanner"
                :initialize-resources-error-banner="
                    initializeResourcesErrorBanner
                "
                :no-active-shift-banner="noActiveShiftBanner"
            />

            <q-page class="main-content">
                <!-- Breadcrumbs -->
                <div
                    v-if="$q.platform.is.desktop && $q.screen.gt.sm"
                    class="q-ma-md vertical-bottom breadcrumbs"
                >
                    <div class="row" style="justify-content: space-between">
                        <q-breadcrumbs id="breadcrumbs">
                            <template v-slot:separator>
                                <q-icon
                                    class="breadcrumbs__separator"
                                    name="chevron_right"
                                    size="1.5em"
                                />
                            </template>
                            <q-breadcrumbs-el
                                v-for="(breadcrumb, index) in breadcrumbs"
                                @click="navigateTo(breadcrumb, index)"
                                :key="index"
                                :label="breadcrumb.label"
                                :class="
                                    index === breadcrumbs.length - 1
                                        ? 'breadcrumbs__item-active'
                                        : 'breadcrumbs__item-normal'
                                "
                            />
                        </q-breadcrumbs>
                    </div>
                </div>
                <q-separator v-if="$q.screen.gt.sm" :inset="true" />

                <!-- Current router view -->
                <div class="q-ma-md">
                    <router-view />
                </div>

                <!-- Loading animation -->
                <loader :show="isLoading" :text="loadingText" />

                <!-- Notification hub -->
                <notification-hub
                    v-if="showNotification"
                    :notifications="getUnresolvedNotifications"
                    :qualityNotifications="getUnresolvedQualityNotifications"
                />
                <!-- Intercom message launcher -->
                <IntercomWidget />
            </q-page>
        </q-page-container>
    </q-layout>
</template>

<script>
import GlobalBanners from '@components/GlobalBanners.vue';
import Loader from '@components/Loader.vue';
import NotificationHub from '@components/NotificationHub.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { ASSET_STATUS_TO_STYLE } from '@models/assets.js';
import ConnectionManager from '@src/connectionManager.js';
import NavBar from './NavBar.vue';
import { useSettingsStore } from '@stores/settings.js';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useProductsStore } from '@stores/products.js';
import { useReportsStore } from '@stores/reports.js';
import { useBannersStore } from '@stores/banners.js';
import { useAuthStore } from '@stores/auth';
import { useLanguagesStore } from '@stores/languages.js';
import { useNotificationsStore } from '@stores/notifications.ts';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { useAssetsStore } from '@stores/assets.ts';
import { useQualityEventsStore } from '@stores/qualityEvents.ts';
import IntercomWidget from '@components/IntercomWidget.vue';

export default {
    name: 'Layout',
    components: {
        Loader,
        NavBar,
        NotificationHub,
        GlobalBanners,
        IntercomWidget
    },
    mixins: [BreadcrumbsManager],
    async created() {
        // Initialize the application
        this.initialize();
        // auto refresh JWT
        this.autoRefreshJWTAction();
    },
    watch: {
        /**
         * Manage route navigation.
         */
        $route: {
            deep: true,
            immediate: true,
            async handler(route) {
                // subscribe user to asset group if visiting the dashboard
                if (route.name === 'asset_dashboard') {
                    const assetId = route.params.assetId;
                    try {
                        // subscribe to asset, handle when subscribing does not work
                        await this.subscribeToAsset(assetId);
                    } catch (error) {
                        this.displaySubscribeBannerAction();
                        // set reconnect function callable with this assetId
                        this.subscribeBanner.reconnectFunction = async () => {
                            await this.subscribeToAsset(assetId);
                        };
                    }
                } else if (route.name === 'facility_dashboard') {
                    const facilityId = route.params.facilityId;
                    try {
                        // subscribe to asset, handle when subscribing does not work
                        await this.subscribeToFacility(facilityId);
                    } catch (error) {
                        this.displaySubscribeBannerAction();
                        // set reconnect function callable with this assetId
                        this.subscribeBanner.reconnectFunction = async () => {
                            await this.subscribeToFacility(facilityId);
                        };
                    }
                }
            }
        },
        /**
         * On initialization and facility changes, fetch the asset phases.
         */
        facilityId: {
            immediate: true,
            async handler(val) {
                if (val) {
                    // clear the notification hub before fetching new notifications
                    this.resetStateAction();
                    await this.loadCurrentShiftUnjustifiedNotificationsAction(
                        val
                    );

                    await this.getAssetPhases();
                }
            }
        },
        /**
         * When user changes their language, update i18n & Quasar language pack.
         */
        userLang: {
            immediate: true,
            handler(newLang) {
                if (newLang) {
                    // set i18n locale
                    this.$i18n.locale = newLang;
                    // update language pack used by Quasar
                    const languages = this.getLanguages.filter(
                        lang => lang.value === newLang
                    );

                    if (languages.length > 0) {
                        const isoName = languages[0]['isoName'];
                        import('quasar/lang/' + isoName).then(langPack => {
                            this.$q.lang.set(langPack.default);
                        });
                    }
                }
            }
        }
    },
    data() {
        return {
            // models
            ASSET_STATUS_TO_STYLE,
            // left panel
            showLeftPanel: false,

            // SignalR HubConnection object
            connection: null
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useAssetsStore, {
            getAssetByUUID: 'getAssetByUUID',
            getAssetProduct: 'getAssetProduct',
            getAssetStatus: 'getAssetStatus',
            selectedAsset: 'getSelectedAsset',
            getAssetPhase: 'getAssetPhase'
        }),
        ...mapState(useNotificationsStore, {
            getUnresolvedNotifications: 'getUnresolvedNotifications',
            getUnresolvedQualityNotifications:
                'getUnresolvedQualityNotifications',
            hasAnyUnresolvedNotifications: 'hasAnyUnresolvedNotifications',
            showNotificationPopup: 'showNotificationPopup'
        }),
        ...mapState(useSharedStore, ['isLoading', 'loadingText']),
        ...mapState(useReportsStore, ['isLoadingReport']),
        ...mapState(useAuthStore, {
            user: 'user',
            followedAssetUUIDs: 'getFollowedAssetUUIDS'
        }),
        ...mapState(useLanguagesStore, ['getLanguages']),
        ...mapState(useFacilitiesStore, {
            getShiftsByFacility: 'getShiftsByFacility',
            hasActiveShift: 'hasActiveShift',
            getFacilities: 'getFacilities',
            getSelectedFacility: 'getSelectedFacility'
        }),
        ...mapState(useAssetsStore, ['getAssets']),
        /**
         * Current user's language code.
         * @returns {(string|null)}
         */
        userLang() {
            return this.user ? this.user.language : null;
        },
        /**
         * Assets for selected facility.
         * @return {Object[]} - List of assets.
         */
        assets() {
            // ensure a facility has been selected
            if (this.selectedFacility === null) {
                return [];
            }
            return this.getAssets(this.selectedFacility['id']);
        },
        /**
         * Facilities list.
         * @return {Object[]} - List of facilities.
         */
        facilities() {
            return this.getFacilities;
        },
        /**
         * Selected facility object.
         * @return {Object|null} - Selected facility.
         */
        selectedFacility() {
            return this.getSelectedFacility;
        },
        /**
         * Dropdown option selected.
         * @return {Object} - Option label and value.
         */
        dropdownOption: {
            // when selected facility in store changes, update the selected option in the dropdown
            get() {
                if (this.selectedFacility) {
                    return {
                        label: this.selectedFacility['name'],
                        value: this.selectedFacility
                    };
                } else {
                    return {
                        label: this.$t('Select Facility'),
                        value: null
                    };
                }
            },
            // when changing the option selected in the dropdown, update selected facility in store
            set(selected) {
                this.selectFacilityAction(selected['value']['id']);

                this.handleRouting({
                    label: selected.label,
                    name: 'facility_dashboard',
                    params: {
                        facilityId: selected.value.id
                    }
                });
            }
        },
        /**
         * Facility options for selector component.
         * @return {Object[]} - List of facilities with additional field 'label' (for Quasar component q-select).
         */
        facilityOptions() {
            const facilityOptions = [];
            this.facilities.forEach(facility => {
                facilityOptions.push({
                    label: facility.name,
                    value: facility
                });
            });
            return facilityOptions;
        },
        /**
         * Get ID of selected facility.
         * @return {(Number|null)}
         */
        facilityId() {
            if (this.selectedFacility) {
                return this.selectedFacility.id;
            } else {
                return null;
            }
        },
        /**
         * Determines if there are any unresolved notifications or quality notifications.
         * @returns {boolean} - Returns true if there are unresolved notifications or quality notifications, otherwise false.
         */
        showNotification() {
            return (
                this.hasAnyUnresolvedNotifications && this.showNotificationPopup
            );
        },
        /**
         * Banner to show when SignalR connection fails.
         * @return {Object} - Banner configuration.
         */
        signalRConnectionFailedBanner() {
            return {
                isLoading: false,
                // reconnect signalr + register handlers
                reconnectFunction: async () => {
                    try {
                        // begin loading animation
                        this.beginLoading(
                            `${this.$t('Initializing application')}...`
                        );
                        // do not use this with the then() method, as initSignalRConnection does not return a promise, so errors propagated will not go to the catch block!
                        await this.initSignalRConnection();
                    } catch (error) {
                        console.error(
                            'Failed to reconnect SignalR due to: ',
                            error
                        );
                        this.displaySignalRConnectionFailedBannerAction();
                    } finally {
                        // stop loading animation
                        this.stopLoading();
                    }
                },
                icon: 'fas fa-exclamation-circle',
                message: this.$t(
                    'Data stream connection expired. Please reconnect to receive live data.'
                )
            };
        },
        /**
         * Banner to show when subscribing to an asset fails.
         * @return {Object} - Banner configuration.
         */
        subscribeBanner() {
            return {
                isLoading: false,
                reconnectFunction: null, // this function is set in the watch hook, as we need to get the assetId from the connection manager method that raised the exception
                icon: 'fas fa-exclamation-circle',
                message: this.$t(
                    'Failed to connect to receive data for this asset. Please reconnect to receive live data.'
                )
            };
        },
        /**
         * Banner to show when SignalR is reconnecting.
         * @return {Object} - Banner configuration.
         */
        reconnectingBanner() {
            return {
                icon: 'fas fa-exclamation-circle',
                message: this.$t(
                    'Data stream connection expired. Attempting to reconnect...'
                )
            };
        },
        /**
         * Banner to show when important resources fail to load.
         * @return {Object} - Banner configuration.
         */
        initializeResourcesErrorBanner() {
            return {
                isLoading: false,
                icon: 'fas fa-exclamation-circle',
                message: this.$t(
                    'We could not load some important information needed to run the app. Please try again.'
                ),
                reconnectFunction: async () => {
                    try {
                        await this.loadAdditionalResources();
                    } catch (error) {
                        console.error(
                            'Failed to load specific resources: ',
                            error
                        );
                    }
                }
            };
        },
        /**
         * Banner to show when there is no active shift for the current time period.
         * @return {Object} - Banner configuration.
         */
        noActiveShiftBanner() {
            if (!this.facilityId) {
                return {
                    show: false
                };
            }

            // Get facility to access its timezone
            const facility = this.getFacility(this.facilityId);
            const facilityTimezone = facility?.timezone || 'UTC';

            const hasActive = this.hasActiveShift(
                this.facilityId,
                facilityTimezone
            );

            // null means shifts not yet loaded, don't show banner
            // false means no active shift for current time, show banner
            // true means there is an active shift, don't show banner
            const shouldShow = hasActive === false;

            return {
                show: shouldShow,
                icon: 'fas fa-clock',
                message: this.$t(
                    "No active shift is defined for this period. Data from these devices won't be recorded. If this is an error, assign a shift in the Shift Management page."
                ),
                actionLabel: this.$t('Manage Shifts'),
                action: async () => {
                    await this.$router.push({
                        name: 'shift_list',
                        params: {
                            facilityId: this.facilityId
                        }
                    });
                }
            };
        }
    },
    methods: {
        ...mapActions(useSettingsStore, ['addAnnouncement', 'loadSettings']),
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useProductsStore, [
            'loadProductCategoriesAction',
            'loadProductPropertiesAction'
        ]),
        ...mapActions(useBannersStore, [
            'displaySignalRConnectionFailedBannerAction',
            'dismissSignalRConnectionFailedBannerAction',
            'displayReconnectingBannerAction',
            'dismissReconnectingBannerAction',
            'displaySubscribeBannerAction',
            'dismissSubscribeBannerAction',
            'displayInitializeResourcesErrorBannerAction',
            'dismissInitializeResourcesErrorBannerAction'
        ]),
        ...mapActions(useAuthStore, ['autoRefreshJWTAction', 'logoutAction']),
        ...mapActions(useLanguagesStore, ['loadLanguagesAction']),
        ...mapActions(useFacilitiesStore, ['selectFacilityAction']),
        ...mapActions(useAssetsStore, [
            'selectAssetAction',
            'setAssetPhaseAction',
            'setDowntimeCauseAction',
            'loadCurrentPhasesAction'
        ]),
        ...mapActions(useQualityEventsStore, [
            'setQualityEventAction',
            'removeQualityEventAction'
        ]),
        ...mapActions(useNotificationsStore, [
            'resolveNotificationAction',
            'resetStateAction',
            'addNotificationAction',
            'addQualityNotificationAction',
            'resolveQualityNotificationAction',
            'loadCurrentShiftUnjustifiedNotificationsAction'
        ]),
        /**
         * Initialize the application.
         */
        async initialize() {
            this.beginLoading(`${this.$t('Initializing application')}...`);

            // Start loading all three resources in parallel
            const initPromise = this.initSignalRConnection();
            const loadSettingsPromise = this.loadClientSettings();
            const loadResourcesPromise = this.loadAdditionalResources();

            try {
                await Promise.all([initPromise, loadSettingsPromise]);
            } catch (err) {
                console.error('Initialization failure:', err);
            }

            loadResourcesPromise.catch(err => {
                console.error('Failed to load additional resources:', err);
            });

            this.stopLoading();
        },

        /**
         * Subscribe to an asset.
         * Note: We handle subscriptions for assets at the layout level to catch connection errors
         *       and show the banner to allow user to re-establish connections.
         * @param {Number} assetId
         */
        async subscribeToAsset(assetId) {
            try {
                await ConnectionManager.subscribe(assetId);
                this.dismissSubscribeBannerAction();
            } catch (error) {
                this.displaySubscribeBannerAction();
                console.error('Failed to subscribe to asset:', error);
                // throw error to be caught by try/catch block in the watcher
                throw error;
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Subscribe to a facility.
         * Note: We handle subscriptions for facilities at the layout level to catch connection errors
         *       and show the banner to allow user to re-establish connections.
         * @param {Number} facilityId
         */
        async subscribeToFacility(facilityId) {
            try {
                await ConnectionManager.subscribeToFacility(facilityId);

                this.dismissSubscribeBannerAction();
            } catch (error) {
                this.displaySubscribeBannerAction();
                console.error('Failed to subscribe to facility:', error);
                // throw error to be caught by try/catch block in the watcher
                throw error;
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Handle initializing connection.
         */
        async initSignalRConnection() {
            try {
                // reset signalR connection banner visibility
                this.dismissSignalRConnectionFailedBannerAction();
                // establish signalr connection
                await ConnectionManager.establishConnection();
                // register phase change handler
                ConnectionManager.addHandler({
                    name: 'startPhase',
                    handler: this.handlePhaseChange
                });
                // register phase change handler
                ConnectionManager.addHandler({
                    name: 'changePhase',
                    handler: this.handlePhaseChange
                });
                // raise notification handler
                ConnectionManager.addHandler({
                    name: 'notifyDowntime',
                    handler: this.handleRaiseDowntimeNotification
                });
                // resolve notification handler
                ConnectionManager.addHandler({
                    name: 'resolveNotification',
                    handler: this.handleResolveDowntimeNotification
                });
                // announcement handler
                ConnectionManager.addHandler({
                    name: 'announcement',
                    handler: this.handleNewAnnouncement
                });
                // set signalr hub callback when connection closes
                ConnectionManager.onClose(async () => {
                    // if the connection closes and the user is not logging out, show the banner
                    if (this.$route.name !== 'login') {
                        this.displaySignalRConnectionFailedBannerAction();
                    }
                });
                // set callback when signalr attempts to reconnect
                ConnectionManager.onReconnecting(() => {
                    console.debug('Signalr reconnecting ...');
                    this.displayReconnectingBannerAction();
                });
                // set callback when signalr has successfully reconnected
                ConnectionManager.onReconnected(async () => {
                    console.debug('Signalr reconnected');
                    this.dismissReconnectingBannerAction();
                    // When a client is disconnected from SignalR, it's possible that asset phases have changed
                    // during this period. We want to re-fetch the phases to ensure that they are up to date.
                    if (this.selectedFacility) {
                        await this.getAssetPhases();
                    }
                });
                // raise quality notification handler
                ConnectionManager.addHandler({
                    name: 'qualityEvent',
                    handler: this.handleQualityNotification
                });
            } catch (error) {
                console.error(
                    'Failed to initialize SignalR connection:',
                    error
                );
                this.displaySignalRConnectionFailedBannerAction();
                // throw error to be caught by try/catch block in initialize method
                throw error;
            }
        },
        /**
         * Load additional resources on startup of the application.
         * We allow this to fail, but we will show a banner to the user to allow them to re-fetch the resources.
         * This is because the application can still partially function without these resources.
         */
        async loadAdditionalResources() {
            try {
                await this.loadProductCategoriesAction();
                await this.loadProductPropertiesAction();
                this.dismissInitializeResourcesErrorBannerAction();
            } catch (error) {
                console.error('Failed to load specific resources: ', error);
                this.displayInitializeResourcesErrorBannerAction();
                // throw error to be caught by try/catch block in initialize method
                throw error;
            }
        },
        /**
         * Store client settings in state.
         * This is a bare minimum requirement for the application to function properly.
         * If it fails, redirect the user to the error page (do not allow the application to be used).
         */
        async loadClientSettings() {
            try {
                await this.loadSettings();
                await this.loadLanguagesAction();
            } catch (error) {
                console.error('Failed to load client settings: ', error);
                // client settings data is a bare minimum requirement for the application
                // if it fails, redirect to error page.
                this.$router.push({ name: 'error' });
            }
        },
        /**
         * Handler for raise downtime notification events.
         * @param {Object} message - Message from signalR.
         */
        async handleRaiseDowntimeNotification(message) {
            try {
                // determine if this user should see this notification
                const followAsset = this.followedAssetUUIDs.includes(
                    message['asset_uuid']
                );

                // check if notification is already in list
                const isNotificationAlreadyInList =
                    this.getUnresolvedNotifications.some(notification => {
                        return notification.eventUUID === message.event_uuid;
                    });

                // if the notification is not already in the list, add it
                if (followAsset && !isNotificationAlreadyInList) {
                    // extract the asset from the store.
                    const asset = this.getAssetByUUID(message['asset_uuid']);
                    if (asset) {
                        // if the asset is present in the store
                        // get phase by asset id
                        const phase = this.getAssetPhase(asset['id']);

                        if (phase) {
                            // get cause from the asset current phase
                            const phaseCause = phase['cause'];

                            // if the phase is present in the store
                            if (!phaseCause) {
                                // if the event don't have cause then raise a notification
                                // build internal format for notifications
                                const notification = {
                                    assetUUID: message['asset_uuid'],
                                    eventUUID: message['event_uuid']
                                };
                                // add notification
                                this.addNotificationAction(notification);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error(
                    'Error while pushing downtime notification to the operating system',
                    error
                );
            }
        },
        /**
         * Handler for resolving downtime notifications.
         *
         * Within the same SignalR tenant group, once a downtime notification is justified by a cause, the cause is
         * propagated to all other clients.
         * @param {Object} message - Message from signalR.
         */
        async handleResolveDowntimeNotification(message) {
            try {
                // internal format
                const data = {
                    eventUUID: message['event_uuid'],
                    username: this.user['username']
                };

                // set the cause of a downtime phase if it's provided
                if ('cause_id' in message) {
                    // We only set the cause of a downtime phase if this user has this asset in store, which means they are on the facility this asset belongs in.
                    // Otherwise, live updates are not necessary if the user is not on the facility this asset belongs in. No data integrity issue can occur as
                    // this user is unable to view phases for this asset. Once the user switches facility, the latest phases (and causes for downtime phases) will
                    // be fetched from the server.
                    const asset = this.getAssetByUUID(message['asset_uuid']);
                    if (asset) {
                        this.setDowntimeCauseAction({
                            assetId: asset.id,
                            causeId: message.cause_id
                        });
                    }
                }

                // remove notification
                this.resolveNotificationAction(data['eventUUID']);
            } catch (error) {
                console.error(
                    `Error resolving notification due to: ${JSON.stringify(error)}`
                );
            }
        },
        /**
         * Handler for new announcement events.
         * @param {Object} message - Message from signalR.
         */
        handleNewAnnouncement(message) {
            try {
                // save announcements in store
                this.addAnnouncement(message);
            } catch (error) {
                console.error(
                    `Error handling announcement due to: ${JSON.stringify(error)}`
                );
            }
        },
        /**
         * Handles user navigation.
         * @param {Object} route
         * @param {string} route.name
         * @param {Object} route.params
         */
        handleRouting(route) {
            // check if user is logging out
            if (route.name === 'login') {
                this.logoutAction();
            }

            // check if user is navigating back to the home page
            if (route.name === 'facilities') {
                // reset breadcrumbs
                this.setBreadcrumbs([
                    {
                        label: this.$t('Facilities')
                    }
                ]);
            }

            // check if the user is switching facilities
            if (route.name === 'facility_dashboard') {
                // set active facility
                this.selectFacilityAction(route.params.facilityId);
            }

            // check if user is navigating to an asset dashboard
            if (route.name === 'asset_dashboard') {
                // set active asset
                this.selectAssetAction(route.params.assetId);
            }

            // navigate
            this.$router
                .push({
                    name: route.name,
                    params: route.params,
                    query: route.query
                })
                .catch(error => {
                    if (error.name === 'NavigationDuplicated') {
                        // do nothing
                    } else {
                        // log error
                        console.error('error: ', error);
                    }
                });
        },
        /**
         * Handle phase changes.
         * @param {Object} message - Message from signalR.
         */
        handlePhaseChange(message) {
            try {
                const asset = this.getAssetByUUID(message['asset_uuid']);

                // only update phase if the user has already loaded the asset
                if (asset) {
                    const data = {
                        phaseUUID: message['phase_uuid'],
                        assetId: asset.id,
                        cause: null,
                        currentProduct: null,
                        status: message['phase'],
                        start: message['start'],
                        lockedTransitionToRunning:
                            message['locked_transition_to_running'] ?? false
                    };
                    if ('cause_id' in message) {
                        data['cause'] = message['cause_id'];
                    }
                    if ('product_id' in message) {
                        data['currentProduct'] = message['product_id'];
                    }
                    this.setAssetPhaseAction(data);
                }
            } catch (error) {
                console.error('Failed to update asset status due to: ', error);
            }
        },
        /**
         * Get the style to use for a drawer item (containing asset name + status) on the drawer.
         * @return {Object}
         */
        getDrawerItemStyle(assetId) {
            let style = {};
            const status = this.getAssetStatus(assetId);
            if (status) {
                style = this.ASSET_STATUS_TO_STYLE[status];
            } else {
                style = this.ASSET_STATUS_TO_STYLE['unknown'];
            }
            return style;
        },
        /**
         * Get asset statuses.
         */
        async getAssetPhases() {
            // fetch asset phases from the API
            await this.loadCurrentPhasesAction({
                facilityId: this.facilityId
            });
        },

        handleQualityNotification(message) {
            const notification = {
                eventID: message['id'],
                eventUUID: message['uuid'],
                assetUUID: message['asset_uuid'],
                eventAction: message['action'],
                eventDefectID: message['defect_id'],
                tenantSchema: message['tenant_schema'],
                tenantUUID: message['tenant_uuid'],
                assetID: message['asset_id'],
                productID: message['product_id'],
                start: message['start'],
                end: message['end'],
                grossProductCount: message['gross_production'],
                rejectedProductCount: message['rejected_production']
            };
            if (
                !notification['eventID'] ||
                !notification['assetUUID'] ||
                !notification['eventUUID'] ||
                !notification['eventAction']
            ) {
                return;
            }

            try {
                // Verify if user follows the asset
                const followAsset = this.followedAssetUUIDs.includes(
                    notification['assetUUID']
                );

                // check if the notification is already in the list
                const isNotificationAlreadyInList =
                    this.getUnresolvedQualityNotifications.some(
                        qualityNotification => {
                            return (
                                qualityNotification.eventUUID ===
                                notification['eventUUID']
                            );
                        }
                    );

                // Check if the notification event action is 'justify'.
                const isJustificationAction =
                    notification['eventAction'] === 'justify';

                // Display the notification only if the user follows the asset
                // and the notification is not already in the list.
                // Dismiss the notification if the event action is 'justify'.
                if (
                    !followAsset ||
                    (isNotificationAlreadyInList && !isJustificationAction)
                ) {
                    return;
                }

                if (notification['eventAction'] === 'start') {
                    if (notification['assetID']) {
                        this.setQualityEventAction(notification);
                    }
                }
                // Show notification only if the event has ended
                else if (notification['eventAction'] === 'end') {
                    // add quality notification to the store
                    // if the event does not have a defect ID
                    if (!notification['eventDefectID']) {
                        this.addQualityNotificationAction(notification);
                    }

                    // remove quality alert from the store
                    this.removeQualityEventAction(notification['assetID']);
                } else if (notification['eventAction'] === 'justify') {
                    // remove quality notification from the store
                    this.resolveQualityNotificationAction(
                        notification['eventUUID']
                    );

                    // set quality event to display an alert in the asset dashboard
                    this.setQualityEventAction(notification);
                }
            } catch (error) {
                console.error(
                    'Error while pushing quality notification to the operating system',
                    error
                );
            }
        }
    }
};
</script>

<style lang="scss" scoped>
// This class was added to the q-page element to ensure that the total
// height of the page is 100% regardless of the q-page-container padding.
.main-content {
    min-height: calc(100vh - 83px) !important;
}
.active-asset {
    background: $light-blue;
}

.asset-label {
    cursor: pointer;
}

.breadcrumbs {
    font-size: 16px;
    font-weight: 600;
}

.breadcrumbs__item-active {
    color: $primary;
}

.breadcrumbs__item-normal {
    color: $medium-grey;
    font-weight: 500;
    cursor: pointer;
}

.breadcrumbs__separator {
    padding-bottom: 2px;
    color: $medium-grey;
}

.dropdown-item__label {
    color: '#505050';
}

.facility-dropdown {
    width: 90%;
}

.user-dropdown {
    color: #505050;
}
// The style property on the q-drawer adds a padding-top
// to the drawer to ensure that the drawer items are not
// hidden by the navbar. This is necessary because the
// q-header was removed to fix the ResizeObserver loop error.
:deep(.left-panel) {
    padding-top: 51px;
    box-sizing: border-box;
}
// The navbar is fixed to the top of the page for mobile devices.
// This class is added to the q-page-container to ensure that the
// content is not hidden by the navbar.
// Consider safe area for iOS devices (notch and status bar)/
// See: https://quasar.dev/quasar-cli-webpack/developing-cordova-apps/troubleshooting-and-tips#status-bar-and-notch-safe-areas
.safe-area-margin {
    padding-top: calc(constant(safe-area-inset-top) + $navbar-height);
    padding-top: calc(env(safe-area-inset-top) + $navbar-height);
}

.safe-area-margin--mobile {
    padding-top: calc(constant(safe-area-inset-top) + $navbar-height-mobile);
    padding-top: calc(env(safe-area-inset-top) + $navbar-height-mobile);
}
</style>
